import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { IconLayer, TextLayer } from 'deck.gl/typed';

import { setHoveredTarget } from 'app/utils/mapStateSlice';
import { setNewChunk, setPlaybackResult } from 'app/utils/facadeReducer';
import { setSelectedTarget } from 'app/utils/selectedTargetsSlice';

import { useAlertHook } from 'app/hooks';
import { svgToDataURL } from 'app/helpers';
import { getVesselIcon } from 'helpers/getWebGLPointIcon';

import { useAppDispatch, useAppSelector } from 'app/store/store';
import { useGetPlaybackMutation } from 'app/api/facade';

import { PlaybackDatePicker } from 'app/components/PlaybackDatePicker';

import { PlaybackTargetData, ViewportData } from 'ais-facade-api/api';

type prepareDataPerTimeT = { time: number; position: { lat: number; lon: number } };
type GetObjDifferentKeys<T, U> = Omit<T, keyof U> & Omit<U, keyof T>;
type GetObjSameKeys<T, U> = Omit<T | U, keyof GetObjDifferentKeys<T, U>>;
type DeepMergeTwoTypes<T, U> = Partial<GetObjDifferentKeys<T, U>> & { [K in keyof GetObjSameKeys<T, U>]: T[K] | U[K] };

export const usePlaybackLayer = (
  zoom: number,
  viewState: any,
  isPlaybackMode: boolean,
  setShowLoader: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  const { t } = useTranslation();
  const { addAlert } = useAlertHook();
  const dispatch = useAppDispatch();
  const bbox = useAppSelector((state) => state.mapStateSlice.bbox);
  const playbackResult = useAppSelector((state) => state.facadeSlice.playbackResult);

  const [getplayback, playbackReq] = useGetPlaybackMutation();

  const [dataById, setDataById] = useState<Record<number, PlaybackTargetData[]>>({});
  const [requestsResArray, setRequestsResArray] = useState<(PlaybackTargetData | undefined)[]>([]);
  const [startDate, setStartDate] = useState<Date>(moment().subtract(1, 'day').toDate());
  const [play, setPlay] = useState(false);
  const [repeat, setRepeat] = useState(false); // Состояние для повтора анимации
  const [progress, setProgress] = useState(0);
  const [currentAnimationTime, setCurrentAnimationTime] = useState<string | null>(null);
  const [firstAnimationDate, setFirstAnimationDate] = useState<number | null>(null);
  const [lastAnimationDate, setLastAnimationDate] = useState<number | null>(null);
  const [animationKey, setAnimationKey] = useState(0);
  const [isAnimationCompleted, setIsAnimationCompleted] = useState(false);
  const [isZoomValid, setIsZoomValid] = useState(false);
  const isZoomEnabled = !play;
  let maxAnimationEndTime = 0; //возможно стоит заменить на useRef

  const playbackPicker = (
    <PlaybackDatePicker
      currentAnimationTime={currentAnimationTime}
      progress={progress}
      setPlay={setPlay}
      setDate={setStartDate}
      selected={startDate}
      disabled={play}
      repeat={repeat}
      setRepeat={setRepeat}
    />
  );

  const prepareDataPerTime = (mmsi: number): DeepMergeTwoTypes<prepareDataPerTimeT, PlaybackTargetData>[] => {
    //TODO: Изменить работу функции так, что бы не использовать время как ключ объекта.
    // Это не позволяет использовать ключ как индекс из-за разницы типов данных

    const startTime = moment(startDate).valueOf(); //стартовое время в милисек
    const positionsPerTime: { [key: number]: PlaybackTargetData } = dataById?.[mmsi];
    const objectTimePosition: DeepMergeTwoTypes<prepareDataPerTimeT, PlaybackTargetData>[] = Object.keys(positionsPerTime).map(
      (timeAsKey) => {
        const diff = moment(timeAsKey).diff(startTime);

        // пусть вся анимация идет 60сек - пока хардкод
        // всего 48 запросов по 30 минут -> 1440 минут -> 250*60 cек
        // 1 минута анимации = 1440 минут в реальности
        const diffInAnimationSecs = diff / 1440; // через сколько милисекунд подложить новые данные относительно старта анимации
        const _Index: string = timeAsKey;
        return {
          time: diffInAnimationSecs,
          ...positionsPerTime[_Index as any],
        };
      },
    );
    return objectTimePosition;
  };

  const layerPlaybackVessels = new IconLayer({
    id: 'playback-layer-vessels',
    data: playbackResult,
    pickable: true,

    getPosition: (d: ViewportData): [number, number, number] => {
      const value = Object.values(d)[0];
      if (typeof value?.lon === 'number' && typeof value?.lat === 'number') return [value.lon, value.lat, 0];
      return [0, 0, 10];
    },

    getAngle: (d: { key: Required<Pick<PlaybackTargetData, 'heading' | 'cog' | 'registrySpecialType'>> }) => {
      const value: Required<Pick<PlaybackTargetData, 'heading' | 'cog' | 'registrySpecialType'>> = Object.values(d)[0];
      return value.registrySpecialType === 'ATON' ? 0 : -value.heading || -value.cog;
    },

    getIcon: (d) => {
      return {
        url: svgToDataURL(getVesselIcon(Object.values(d)[0] as PlaybackTargetData, true, true)),
        width: 28,
        height: 28,
      };
    },
    sizeMinPixels: 28,
    transitions: {
      getPosition: 500,
    },
    updateTriggers: {
      getPostition: play ? { playbackResult } : null,
    },
    visible: isPlaybackMode,
    onClick(pickingInfo) {
      dispatch(
        setSelectedTarget({
          target: { ...pickingInfo, object: Object.values(pickingInfo.object)[0] },
          type: pickingInfo.object.registrySpecialType === 'ATON' ? 'aton' : 'vessel',
        }),
      );
    },
    onHover(pickingInfo) {
      if (pickingInfo.object) {
        dispatch(setHoveredTarget({ ...pickingInfo, object: Object.values(pickingInfo?.object)[0] }));
      }
    },
  });

  const layerPlaybackVesselsNames = new TextLayer({
    id: 'playback-layer-vessels-names',
    data: playbackResult,
    pickable: false,
    getKey: (d: ViewportData) => {
      const key = Object.keys(d);
      return key;
    },
    getPosition: (d: ViewportData): [number, number, number] => {
      const value = Object.values(d)[0];
      if (typeof value?.lon === 'number' && typeof value?.lat === 'number') return [value.lon, value.lat, 0];
      return [0, 0, 10];
    },
    getText: (d: ViewportData) => {
      const value = Object.values(d)[0];
      if (typeof value?.name === 'string') return value.name;
      return 'text';
    },
    getColor: [255, 255, 255, 255],
    getSize: 10,
    getAngle: 0,
    getTextAnchor: 'start',
    getAlignmentBaseline: 'bottom',
    visible: isPlaybackMode && viewState.zoom > 6,
    transitions: {
      getPosition: 500,
    },
    updateTriggers: {
      getPostition: play ? { playbackResult } : null,
    },
  });

  useEffect(() => {
    setIsZoomValid(viewState.zoom >= 11);
  }, [viewState.zoom]);

  useEffect(() => {
    const getVesselsDataByTimestamps = async (timestamps: string[]) => {
      try {
        const promises = timestamps.map((ts) => {
          if (bbox.first && bbox.second && isZoomValid) {
            const toInterval = moment(ts).utc().add(30, 'minute').toISOString();
            setShowLoader(true);
            return getplayback({
              filter: { bbox },
              interval: {
                from: ts,
                to: toInterval,
              },
              datetime: ts + '/' + toInterval,
            }).unwrap();
          }
        });

        const results = await Promise.allSettled(promises);

        const requestsRes: (PlaybackTargetData | undefined)[] = results
          .map((result) => {
            if (result.status === 'fulfilled') {
              return result?.value?.results;
            } else if (result.status === 'rejected') {
              addAlert(result.reason);
              setPlay(false);
              setShowLoader(false);
              return [];
            }
          })
          .flat();

        if (requestsRes.length === 0) {
          addAlert({
            data: {
              messages: [{ description: t('NoPlayBackData'), type: 'information' }],
            },
          });
        }

        setRequestsResArray(requestsRes);
      } catch (e) {
        setPlay(false);
      }
    };
    if (isPlaybackMode && play && isZoomValid) {
      dispatch(setPlaybackResult([]));
      const timestamps = [];
      for (let i = 0; i < 24 * 2; i++) {
        const newTmsp = moment(startDate)
          .utc()
          .add(30 * i, 'minute')
          .toISOString();
        timestamps.push(newTmsp);
      }
      getVesselsDataByTimestamps(timestamps);
    } else {
      setDataById({});
      setShowLoader(false);
      playbackReq.reset();
    }
  }, [startDate, play, isZoomValid]);

  useEffect(() => {
    if (requestsResArray.length > 0) {
      let dataByIdLocal: Record<number, any> = {};
      requestsResArray?.forEach((entry: any) => {
        if (!entry) return;
        if (entry.mmsi && entry.t && dataById[entry.mmsi]) {
          if (!dataByIdLocal[entry.mmsi]) dataByIdLocal[entry.mmsi] = {};
          dataByIdLocal[entry.mmsi][entry.t] = entry;

          const newMmsiData = { [entry.mmsi]: { ...dataById[entry.mmsi], ...dataByIdLocal[entry.mmsi] } };
          const newdata = { ...dataById, ...dataByIdLocal, ...newMmsiData };
          dataByIdLocal = newdata;
        } else {
          if (entry.mmsi && !dataByIdLocal[entry.mmsi]) dataByIdLocal[entry.mmsi] = {};
          dataByIdLocal[entry.mmsi][entry.t] = entry;

          const newdata = { ...dataById, ...dataByIdLocal };
          dataByIdLocal = newdata;
        }
      });

      setDataById(dataByIdLocal);
      const allTimestamps = requestsResArray
        .filter((entry): entry is PlaybackTargetData => !!entry?.t)
        .map((entry) => moment(entry.t).valueOf());

      if (allTimestamps.length > 0) {
        const minTimestamp = Math.min(...allTimestamps);
        const maxTimestamp = Math.max(...allTimestamps);
        setFirstAnimationDate(minTimestamp);
        setLastAnimationDate(maxTimestamp);
      }
    } else {
      setDataById({});
      setShowLoader(false);
      setPlay(false);
    }
  }, [requestsResArray]);

  useEffect(() => {
    let timers: NodeJS.Timeout[][] = [];
    const entries = Object.entries(dataById);

    let completedVessels = 0;
    const totalVessels = entries.length;

    if (entries.length > 0) {
      timers = entries.map((vesselObj: [string, PlaybackTargetData[]]) => {
        const [mmsi] = vesselObj;
        const preparedData = prepareDataPerTime(Number(mmsi));
        const lastTime = preparedData[preparedData.length - 1]?.time || 0;
        maxAnimationEndTime = Math.max(maxAnimationEndTime, lastTime);
        const intervals = preparedData.map((timeElement, index) => {
          const { time, ...elementData } = timeElement;

          return setTimeout(() => {
            const result = { mmsi, ...elementData };
            dispatch(setNewChunk(result));
            const formattedTime = moment(elementData.t).format('DD.MM.YYYY \u2013 HH:mm:ss');
            setCurrentAnimationTime(formattedTime);
            if (index === preparedData.length - 1) {
              completedVessels++;
              if (completedVessels === totalVessels) {
                setIsAnimationCompleted(true);
              }
            }
          }, time);
        });
        return intervals;
      });
      setShowLoader(false);
    }

    return () => {
      timers.flat().forEach((timer) => clearTimeout(timer));
    };
  }, [dataById, animationKey]);

  useEffect(() => {
    if (isAnimationCompleted && repeat) {
      setTimeout(() => {
        setAnimationKey((prev) => prev + 1);
        setIsAnimationCompleted(false);
      }, maxAnimationEndTime);
    }
  }, [isAnimationCompleted, repeat]);

  useEffect(() => {
    if (!firstAnimationDate || !lastAnimationDate || !currentAnimationTime) return;
    const parsedCurrentTime = moment(currentAnimationTime, 'DD-MM-YYYY HH:mm:ss').valueOf();
    const totalDuration = lastAnimationDate - firstAnimationDate;
    const elapsedTime = parsedCurrentTime - firstAnimationDate;

    if (totalDuration > 0) {
      setProgress(Math.min(100, Math.max(0, (elapsedTime / totalDuration) * 100)));
    }
  }, [currentAnimationTime, firstAnimationDate, lastAnimationDate]);

  useEffect(() => {
    if (!play) {
      setProgress(0);
      setCurrentAnimationTime(null);
      setFirstAnimationDate(null);
      setLastAnimationDate(null);
    }
  }, [play]);

  return { layer: [layerPlaybackVessels, layerPlaybackVesselsNames], playbackPicker, isZoomEnabled };
};
